/**
 * Application Insights telemetry (client-side, backendless).
 *
 * AI ingestion is authenticated by its own connection string, NOT the user's
 * Graph/SharePoint token, so telemetry keeps flowing even when the add-in's
 * SharePoint auth is broken - which is exactly when we need to see it.
 *
 * Broad capture by design ("capture all issues"):
 *  - unhandled JS exceptions and promise rejections (auto)
 *  - every fetch/Graph call as a dependency, including failures + status codes (auto)
 *  - SPA page views (auto)
 *  - the app's own logger.warn/logger.error (routed here via a sink)
 *  - targeted custom events at the auth-recovery moments
 *
 * No connection string (local dev) -> every function is a safe no-op.
 */
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { config } from '@/config';
import { setLoggerSink } from '@/utils/logger';

let ai: ApplicationInsights | null = null;

export function initTelemetry(): void {
  if (ai || !config.telemetry?.connectionString) return;
  // One role name per environment so staging and prod are separable in AI even
  // though they share one resource and one connection string. Computed here (not
  // at module load) so importing this file never touches config shape.
  const roleName = `crg-filespace-${config.build.env}`;
  try {
    ai = new ApplicationInsights({
      config: {
        connectionString: config.telemetry.connectionString,
        enableAutoRouteTracking: true, // SPA page views
        disableFetchTracking: false, // capture Graph calls + failures as dependencies
        enableUnhandledPromiseRejectionTracking: true, // catch async errors
        autoTrackPageVisitTime: true,
        disableCookiesUsage: true, // Office webview: avoid cookie dependence
      },
    });
    ai.loadAppInsights();
    // Stamp env + build on every item so we can filter staging vs prod. In the
    // AI v3 SDK the `data` bag is what becomes customDimensions on every item
    // (pageViews, customEvents, exceptions, dependencies); `tags` carries the
    // cloud role. (Writing to data.baseData.properties did NOT attach.)
    ai.addTelemetryInitializer((item) => {
      item.tags = item.tags || {};
      item.tags['ai.cloud.role'] = roleName;
      item.data = item.data || {};
      item.data.env = config.build.env;
      item.data.appVersion = config.build.version;
      if (config.build.sha) item.data.sha = config.build.sha;
    });
    ai.trackPageView();
    // Route the app's logger into AI so all existing warn/error logs are captured.
    setLoggerSink((level, args) => {
      const message = args
        .map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : safeString(a)))
        .join(' ');
      const firstError = args.find((a): a is Error => a instanceof Error);
      if (level === 'error') {
        if (firstError) trackException(firstError, { message });
        else trackTrace(message, SeverityLevel.Error);
      } else {
        trackTrace(message, SeverityLevel.Warning);
      }
    });
  } catch {
    // Never let telemetry setup break the app.
    ai = null;
  }
}

// Associate subsequent events with the signed-in user so per-user auth issues
// (e.g. "did Anne's 401 recover?") are queryable. Internal corporate accounts.
export function setTelemetryUser(oid: string | undefined, upn: string | undefined): void {
  if (!ai || !oid) return;
  try {
    ai.setAuthenticatedUserContext(oid, undefined, true);
    if (upn) ai.context.user.accountId = upn;
  } catch {
    /* no-op */
  }
}

export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  try {
    ai?.trackEvent({ name }, normalizeProps(properties));
  } catch {
    /* no-op */
  }
}

export function trackException(error: unknown, properties?: Record<string, unknown>): void {
  try {
    const exception = error instanceof Error ? error : new Error(safeString(error));
    ai?.trackException({ exception }, normalizeProps(properties));
  } catch {
    /* no-op */
  }
}

export function trackTrace(message: string, severity?: SeverityLevel): void {
  try {
    ai?.trackTrace({ message, severityLevel: severity });
  } catch {
    /* no-op */
  }
}

function normalizeProps(properties?: Record<string, unknown>): Record<string, string> | undefined {
  if (!properties) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(properties)) out[k] = safeString(v);
  return out;
}

function safeString(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
